import type { CameraControls } from "@react-three/drei";
import {
  Box3,
  type BufferGeometry,
  type Group,
  InstancedMesh,
  type Matrix4,
  Mesh,
  type Object3D,
  type PerspectiveCamera,
  SkinnedMesh,
  Spherical,
  Vector3,
} from "three";

const TWO_PI = Math.PI * 2;

/** Smallest signed angle delta in [-π, π]. */
export const normalizeRadiansDelta = (delta: number): number => {
  let normalized = delta % TWO_PI;
  if (normalized > Math.PI) {
    normalized -= TWO_PI;
  }
  if (normalized <= -Math.PI) {
    normalized += TWO_PI;
  }
  return normalized;
};

/** Target azimuth on the shortest arc from current (supports accumulative azimuth). */
export const shortestAzimuthTarget = (
  currentAzimuth: number,
  targetAzimuth: number,
): number =>
  currentAzimuth + normalizeRadiansDelta(targetAzimuth - currentAzimuth);

/**
 * Patch transition end-state on the same tick as `setLookAt` so the animated
 * path uses the shortest azimuth arc (public API only; no `_sphericalEnd`).
 */
const applyShortestAzimuthDuringLookAtTransition = (
  cameraControls: CameraControls,
  adjustedAzimuth: number,
  polarAngle: number,
) => {
  if (typeof cameraControls.normalizeRotations === "function") {
    cameraControls.normalizeRotations();
    return;
  }
  // Stubs without `normalizeRotations`: overwrite end angles before frames advance.
  if (typeof cameraControls.rotateTo === "function") {
    void cameraControls.rotateTo(adjustedAzimuth, polarAngle, true);
  }
};

export const CAMERA_FIT_PADDING = {
  paddingLeft: 0.1,
  paddingRight: 0.1,
  paddingBottom: 0.1,
  paddingTop: 0.1,
};

export const ISOMETRIC_CAMERA_DIRECTION: [number, number, number] = [1, 2, 3];

export const INITIAL_FIT_STABLE_FRAMES = 5;

const PERSPECTIVE_FIT_PADDING_FACTOR = 1.1;
const INITIAL_FIT_BOUNDS_EPSILON = 0.0001;

const getBoxCorners = (box: Box3) => [
  new Vector3(box.min.x, box.min.y, box.min.z),
  new Vector3(box.min.x, box.min.y, box.max.z),
  new Vector3(box.min.x, box.max.y, box.min.z),
  new Vector3(box.min.x, box.max.y, box.max.z),
  new Vector3(box.max.x, box.min.y, box.min.z),
  new Vector3(box.max.x, box.min.y, box.max.z),
  new Vector3(box.max.x, box.max.y, box.min.z),
  new Vector3(box.max.x, box.max.y, box.max.z),
];

export const PRODUCT_SCENE_LOADING_BOUNDS_NAME = "__supply-part-loading-bounds";
export const PRODUCT_SCENE_MESH_LOADS_KEY = "__productSceneMeshLoads";

export const hasPendingLoadingBounds = (object: Group) => {
  let hasPendingBounds = false;
  object.traverse((child) => {
    if (child.name === PRODUCT_SCENE_LOADING_BOUNDS_NAME) {
      hasPendingBounds = true;
    }
  });

  return hasPendingBounds;
};

export const trackProductSceneMeshLoad = (scope: Object3D): (() => void) => {
  const current = scope.userData[PRODUCT_SCENE_MESH_LOADS_KEY] ?? 0;
  scope.userData[PRODUCT_SCENE_MESH_LOADS_KEY] = current + 1;

  return () => {
    const next = (scope.userData[PRODUCT_SCENE_MESH_LOADS_KEY] ?? 1) - 1;
    if (next <= 0) {
      delete scope.userData[PRODUCT_SCENE_MESH_LOADS_KEY];
      return;
    }
    scope.userData[PRODUCT_SCENE_MESH_LOADS_KEY] = next;
  };
};

export const hasPendingSceneMeshLoads = (object: Group) => {
  let pending = false;
  object.traverse((child) => {
    if ((child.userData[PRODUCT_SCENE_MESH_LOADS_KEY] ?? 0) > 0) {
      pending = true;
    }
  });

  return pending;
};

export const hasPendingSceneAssets = (object: Group) =>
  hasPendingLoadingBounds(object) || hasPendingSceneMeshLoads(object);

const _productBoundsTempBox = new Box3();

type GeometryAttributeVersions = {
  positionVersion: number;
  indexVersion: number;
};

type InstancedMeshLocalBoundsCache = {
  count: number;
  geometryUuid: string;
  instanceMatrixVersion: number;
  geometryPositionVersion: number;
  geometryIndexVersion: number;
  geometryPosition: BufferGeometry["attributes"][string] | undefined;
  geometryIndex: BufferGeometry["index"];
  localBox: Box3;
};

const getBufferAttributeVersion = (
  attribute:
    | BufferGeometry["attributes"][string]
    | BufferGeometry["index"]
    | undefined,
): number => {
  if (!attribute || !("version" in attribute)) {
    return -1;
  }
  return attribute.version;
};

const getGeometryAttributeVersions = (
  geometry: BufferGeometry,
): GeometryAttributeVersions => ({
  positionVersion: getBufferAttributeVersion(geometry.attributes.position),
  indexVersion: getBufferAttributeVersion(geometry.index),
});

const geometryAttributesChanged = (
  cache: InstancedMeshLocalBoundsCache,
  geometry: BufferGeometry,
): boolean => {
  const versions = getGeometryAttributeVersions(geometry);
  return (
    cache.geometryPosition !== geometry.attributes.position ||
    cache.geometryIndex !== geometry.index ||
    cache.geometryPositionVersion !== versions.positionVersion ||
    cache.geometryIndexVersion !== versions.indexVersion
  );
};

const instancedMeshLocalBoundsCache = new WeakMap<
  InstancedMesh,
  InstancedMeshLocalBoundsCache
>();

const unionGeometryBoundsWithMatrix = (
  geometryBox: Box3,
  matrix: Matrix4,
  target: Box3,
): void => {
  _productBoundsTempBox.copy(geometryBox);
  _productBoundsTempBox.applyMatrix4(matrix);
  target.union(_productBoundsTempBox);
};

const recomputeInstancedMeshLocalBounds = (
  mesh: InstancedMesh,
  cache: InstancedMeshLocalBoundsCache,
): void => {
  // InstancedMesh.computeBoundingBox() reuses geometry.boundingBox when present;
  // refresh geometry first so in-place vertex edits are reflected.
  mesh.geometry.computeBoundingBox();
  mesh.computeBoundingBox();
  if (!mesh.boundingBox || mesh.boundingBox.isEmpty()) {
    cache.localBox.makeEmpty();
    return;
  }

  cache.localBox.copy(mesh.boundingBox);
  cache.count = mesh.count;
  cache.instanceMatrixVersion = mesh.instanceMatrix.version;
  cache.geometryUuid = mesh.geometry.uuid;
  const geometryVersions = getGeometryAttributeVersions(mesh.geometry);
  cache.geometryPositionVersion = geometryVersions.positionVersion;
  cache.geometryIndexVersion = geometryVersions.indexVersion;
  cache.geometryPosition = mesh.geometry.attributes.position;
  cache.geometryIndex = mesh.geometry.index;
};

const expandInstancedMeshBounds = (mesh: InstancedMesh, target: Box3): void => {
  let cache = instancedMeshLocalBoundsCache.get(mesh);

  if (cache) {
    const needsLocalRecompute =
      cache.count !== mesh.count ||
      cache.instanceMatrixVersion !== mesh.instanceMatrix.version ||
      cache.geometryUuid !== mesh.geometry.uuid ||
      geometryAttributesChanged(cache, mesh.geometry);

    if (needsLocalRecompute) {
      recomputeInstancedMeshLocalBounds(mesh, cache);
    }
  } else {
    const geometryVersions = getGeometryAttributeVersions(mesh.geometry);
    cache = {
      count: mesh.count,
      geometryUuid: mesh.geometry.uuid,
      instanceMatrixVersion: mesh.instanceMatrix.version,
      geometryPositionVersion: geometryVersions.positionVersion,
      geometryIndexVersion: geometryVersions.indexVersion,
      geometryPosition: mesh.geometry.attributes.position,
      geometryIndex: mesh.geometry.index,
      localBox: new Box3(),
    };
    instancedMeshLocalBoundsCache.set(mesh, cache);
    recomputeInstancedMeshLocalBounds(mesh, cache);
  }

  if (cache.localBox.isEmpty()) {
    return;
  }

  _productBoundsTempBox.copy(cache.localBox);
  _productBoundsTempBox.applyMatrix4(mesh.matrixWorld);
  target.union(_productBoundsTempBox);
};

const shouldSkipProductBoundsObject = (object: Object3D): boolean =>
  Boolean(object.userData?.isMeasurementBox || object.userData?.isDragHelper);

const ensureGeometryBoundingBox = (geometry: BufferGeometry): Box3 | null => {
  if (!geometry.boundingBox) {
    geometry.computeBoundingBox();
  }
  return geometry.boundingBox;
};

const expandMeshBounds = (mesh: Mesh, target: Box3): void => {
  const { geometry } = mesh;
  if (!geometry) {
    return;
  }

  if (mesh instanceof SkinnedMesh) {
    mesh.computeBoundingBox();
    if (mesh.boundingBox) {
      unionGeometryBoundsWithMatrix(mesh.boundingBox, mesh.matrixWorld, target);
    }
    return;
  }

  const boundingBox = ensureGeometryBoundingBox(geometry);
  if (boundingBox) {
    unionGeometryBoundsWithMatrix(boundingBox, mesh.matrixWorld, target);
  }
};

const expandInstancedMeshObjectBounds = (
  mesh: InstancedMesh,
  target: Box3,
): void => {
  const { geometry } = mesh;
  if (!geometry) {
    return;
  }

  if (ensureGeometryBoundingBox(geometry)) {
    expandInstancedMeshBounds(mesh, target);
  }
};

/**
 * Expands `target` with the geometry of `object` and its descendants,
 * skipping measurement overlays (dimensions/guide lines) and drag helpers
 * so only the actual product geometry contributes to the bounds.
 *
 * Only unions `Mesh` and `InstancedMesh`. That is intentional for this
 * project: manufacturer/emerge part renderers build solid product volume
 * from meshes (including supply-part loads and `<primitive object={mesh}>`).
 * `Line`/`LineSegments`/`Points` usage is decorative only — e.g. drei
 * `<Edges>` on a parent mesh, AngleSection edge outlines, blueprint
 * animation lines — and sits inside the parent mesh bounds. Overlays and
 * drag helpers are excluded via `userData` above.
 *
 * Mirrors the exclusion logic used by the floor aligner so camera fitting
 * and floor alignment agree on what counts as "the product".
 */
const expandBoxFromProduct = (object: Object3D, target: Box3): void => {
  if (shouldSkipProductBoundsObject(object)) {
    return;
  }

  if (object instanceof InstancedMesh) {
    expandInstancedMeshObjectBounds(object, target);
  } else if (object instanceof Mesh) {
    expandMeshBounds(object, target);
  }

  for (const child of object.children) {
    expandBoxFromProduct(child, target);
  }
};

/**
 * Bounding box of the product geometry only, excluding measurement overlays
 * and drag helpers. Callers must ensure world matrices are up to date.
 */
export const computeProductBoundingBox = (
  object: Object3D,
  target: Box3 = new Box3(),
): Box3 => {
  target.makeEmpty();
  expandBoxFromProduct(object, target);
  return target;
};

export const areBoxesNearlyEqual = (a: Box3, b: Box3) =>
  a.min.distanceToSquared(b.min) <= INITIAL_FIT_BOUNDS_EPSILON &&
  a.max.distanceToSquared(b.max) <= INITIAL_FIT_BOUNDS_EPSILON;

export type StableBoundsTracker = {
  stableFrameCount: number;
  lastBox: Box3 | null;
};

export const createStableBoundsTracker = (): StableBoundsTracker => ({
  stableFrameCount: 0,
  lastBox: null,
});

export const resetStableBoundsTracker = (
  tracker: StableBoundsTracker,
): void => {
  tracker.stableFrameCount = 0;
  tracker.lastBox = null;
};

/**
 * Counts consecutive frames whose bounds stay within epsilon of the anchor
 * box at the start of the current streak. The anchor is only replaced when
 * bounds change enough to break stability, so slow per-frame expansion during
 * loading cannot accumulate as "stable".
 */
export const advanceStableBoundsTracker = (
  currentBox: Box3,
  tracker: StableBoundsTracker,
): boolean => {
  if (tracker.lastBox && areBoxesNearlyEqual(currentBox, tracker.lastBox)) {
    tracker.stableFrameCount += 1;
  } else {
    tracker.stableFrameCount = 1;
    tracker.lastBox = currentBox.clone();
  }
  return tracker.stableFrameCount >= INITIAL_FIT_STABLE_FRAMES;
};

export const waitForNextAnimationFrame = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve());
  });

export const getObjectBoundingBoxCenter = (object: Group): Vector3 | null => {
  // Refresh world matrices so the bounds reflect the current transform even
  // when called before the next render tick (computeProductBoundingBox reads
  // matrixWorld directly).
  object.updateMatrixWorld(true);
  const box = computeProductBoundingBox(object);
  if (box.isEmpty()) {
    return null;
  }

  return box.getCenter(new Vector3());
};

export const fitPerspectiveCameraToBox = async (
  cameraControls: CameraControls,
  camera: PerspectiveCamera,
  object: Group,
  direction: [number, number, number],
  enableTransition = true,
) => {
  // Refresh world matrices so the fit reflects the current transform even when
  // called before the next render tick (computeProductBoundingBox reads
  // matrixWorld directly).
  object.updateMatrixWorld(true);
  const box = computeProductBoundingBox(object);
  if (box.isEmpty()) return;

  const center = box.getCenter(new Vector3());
  const viewDirection = new Vector3(...direction).normalize();
  const forward = viewDirection.clone().negate();
  const worldUp =
    Math.abs(forward.dot(new Vector3(0, 1, 0))) > 0.999
      ? new Vector3(0, 0, 1)
      : new Vector3(0, 1, 0);
  const right = forward.clone().cross(worldUp).normalize();
  const up = right.clone().cross(forward).normalize();
  const tanHalfVerticalFov = Math.tan((camera.fov * Math.PI) / 360);
  const tanHalfHorizontalFov = tanHalfVerticalFov * camera.aspect;

  const distance = getBoxCorners(box).reduce((requiredDistance, corner) => {
    const relativeCorner = corner.sub(center);
    const depthOffset = relativeCorner.dot(viewDirection);
    const horizontalDistance =
      Math.abs(relativeCorner.dot(right)) / tanHalfHorizontalFov + depthOffset;
    const verticalDistance =
      Math.abs(relativeCorner.dot(up)) / tanHalfVerticalFov + depthOffset;

    return Math.max(requiredDistance, horizontalDistance, verticalDistance);
  }, camera.near);

  const paddedDistance = Math.max(
    distance * PERSPECTIVE_FIT_PADDING_FACTOR,
    camera.near,
  );
  const cameraPosition = center
    .clone()
    .add(viewDirection.multiplyScalar(paddedDistance));

  const offset = cameraPosition.clone().sub(center);
  const targetSpherical = new Spherical().setFromVector3(offset);
  targetSpherical.makeSafe();

  const adjustedAzimuth = shortestAzimuthTarget(
    cameraControls.azimuthAngle,
    targetSpherical.theta,
  );

  // `setOrbitPoint` (triggered by clicking a rotation point) installs a focal
  // offset to keep the camera visually fixed while shifting the orbit center.
  // `setLookAt` does not clear that offset, so any later view fit would stay
  // shifted off-center. Reset it so framing re-centers on the box.
  if (typeof cameraControls.setFocalOffset === "function") {
    void cameraControls.setFocalOffset(0, 0, 0, enableTransition);
  }

  const transitionPromise = cameraControls.setLookAt(
    cameraPosition.x,
    cameraPosition.y,
    cameraPosition.z,
    center.x,
    center.y,
    center.z,
    enableTransition,
  );

  if (enableTransition) {
    applyShortestAzimuthDuringLookAtTransition(
      cameraControls,
      adjustedAzimuth,
      targetSpherical.phi,
    );
  }

  await transitionPromise;
};
